import { useEffect, useRef } from 'react';
import { loadYouTubeApi, type YTPlayer } from '../lib/loadScript';
import type { PlaybackState } from '../lib/types';
import type { Source } from '../lib/parseUrl';

interface Props {
  source: Extract<Source, { provider: 'youtube' }>;
  muted: boolean;
  onState: (s: PlaybackState) => void;
}

/**
 * 公式 IFrame Player API のみを使う。
 *
 * 音量と再生/停止はプレイヤー純正の UI に任せ、こちらからは触らない。
 * このアプリが制御するのは「どのタイルから音を出すか」だけ。
 *
 * ライフサイクル上の重要な前提:
 *
 *  1. **iframe は React ではなくこの effect が所有する。**
 *     YT.Player.destroy() は iframe 要素そのものを DOM から取り除く。React が
 *     iframe を描画していると、React は「src が変わっていないので再生成不要」と
 *     判断して作り直さないため、destroy 後は二度と復活せず黒画面のまま残る。
 *
 *  2. **effect の依存は「どの動画を出すか」だけにする。**
 *     コールバックを依存に入れると、親の再レンダリングのたびに effect が
 *     破棄・再実行され、そのたびにプレイヤーが作り直される。
 *
 *  3. **playerRef には onReady 後のインスタンスだけを入れる。**
 *     new YT.Player() の戻り値には ready 前は mute 等が生えていない。
 */
export function YouTubePlayer({ source, muted, onState }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<YTPlayer | null>(null);
  const rawRef = useRef<YTPlayer | null>(null);

  const onStateRef = useRef(onState);
  onStateRef.current = onState;
  const mutedRef = useRef(muted);
  mutedRef.current = muted;

  const embedUrl = buildEmbedUrl(source);
  const title = source.kind === 'video' ? `YouTube ${source.videoId}` : 'YouTube Live';

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let disposed = false;

    const iframe = document.createElement('iframe');
    iframe.src = embedUrl;
    iframe.title = title;
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    iframe.allowFullscreen = true;
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    iframe.setAttribute('frameborder', '0');
    container.replaceChildren(iframe);

    loadYouTubeApi()
      .then((YT) => {
        if (disposed) return;
        rawRef.current = new YT.Player(iframe, {
          events: {
            onReady: ({ target }) => {
              if (disposed) return;
              playerRef.current = target;
              applyMuted(target, mutedRef.current);
            },
            onStateChange: ({ data }) => onStateRef.current(mapState(data)),
            onError: () => onStateRef.current('error'),
          },
        });
      })
      .catch(() => onStateRef.current('error'));

    return () => {
      disposed = true;
      call(rawRef.current, 'destroy');
      rawRef.current = null;
      playerRef.current = null;
      container.replaceChildren();
    };
  }, [embedUrl, title]);

  useEffect(() => {
    const p = playerRef.current;
    if (p) applyMuted(p, muted);
  }, [muted]);

  return (
    <div className="player-frame">
      <div ref={containerRef} className="player-host" />
    </div>
  );
}

/** ready 前・破棄後はメソッド自体が存在しないことがあるため、存在確認してから呼ぶ。 */
function call(p: YTPlayer | null, method: keyof YTPlayer): void {
  if (!p) return;
  const fn = p[method];
  if (typeof fn !== 'function') return;
  try {
    (fn as () => void).call(p);
  } catch {
    /* iframe が既に外れている場合は無視 */
  }
}

function applyMuted(p: YTPlayer, muted: boolean) {
  call(p, muted ? 'mute' : 'unMute');
}

function mapState(code: number): PlaybackState {
  switch (code) {
    case 1:
    case 3:
      return 'playing';
    case 2:
      return 'paused';
    case 0:
      return 'ended';
    default:
      return 'idle';
  }
}

function buildEmbedUrl(source: Extract<Source, { provider: 'youtube' }>): string {
  const params = new URLSearchParams({
    enablejsapi: '1',
    playsinline: '1',
    rel: '0',
    origin: window.location.origin,
  });

  if (source.kind === 'video') {
    return `https://www.youtube.com/embed/${source.videoId}?${params.toString()}`;
  }
  params.set('channel', source.channelId);
  return `https://www.youtube.com/embed/live_stream?${params.toString()}`;
}
